/**
 * Shared formatting helpers for the Admin surface.
 *
 * Kept here so AdminPage and its split-out child components (rows,
 * dialogs, toolbars) all surface 422 validation errors the same way
 * and read timestamps consistently.
 */

/**
 * Surface user-facing API errors as a single string. FastAPI 422
 * validation returns an array of `{msg,...}` objects; rendering that
 * directly crashes React. (Pattern from the auth integration playbook.)
 */
export function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  }
  return fallback || "Something went wrong";
}

/**
 * Render an ISO timestamp as a human-friendly relative phrase ("3 days
 * ago"). Returns null when the input is empty so callers can show their
 * own "Never" placeholder without coupling to date logic.
 */
export function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const diffMs = Date.now() - then.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/**
 * True when the user's `locked_until` ISO timestamp is still in the
 * future. Used by the role filter, the locked-only toggle, and the row
 * badge.
 */
export function isUserLocked(u) {
  if (!u?.locked_until) return false;
  try { return new Date(u.locked_until) > new Date(); } catch { return false; }
}
